import React, { useEffect, useState } from 'react';
import styled from '@emotion/styled';
import { css } from '@emotion/react';
import { Input } from '@components/atoms';

interface UserPermissionProps {
  targetPermission?: string;
  setTargetPermission?: any;
}

const UserPermission = ({ targetPermission, setTargetPermission }: UserPermissionProps) => {
  const [isUploadRead] = useState(true);
  const [isDelete, setIsDelete] = useState(false);
  const [readAll, setReadAll] = useState(false);
  const [deleteAll, setDeleteAll] = useState(true);

  useEffect(() => {
    if (!!targetPermission?.length) {
      if (targetPermission.includes('READ_')) {
        if (targetPermission.includes('READ_ALL')) setReadAll(true);
        else setDeleteAll(false);
      }

      if (targetPermission.includes('DELETE_')) {
        setIsDelete(true);
        if (targetPermission.includes('DELETE_ALL')) setDeleteAll(true);
        else setDeleteAll(false);
      }
    }
  }, []);

  const createPermissionString = (
    DRU: boolean, // Data Upload & Read
    RA: boolean, // Read All
    D: boolean, // Delete
    DA: boolean, // Delete All
  ) => {
    if (!DRU) return '';
    const permissionSets = [`READ_${RA ? 'ALL' : 'MY'}`];

    if (D) {
      permissionSets.push(`DELETE_${RA && DA ? 'ALL' : 'MY'}`);
    }
    return permissionSets.join('_');
  };

  useEffect(() => {
    if (setTargetPermission) {
      const newPermission = createPermissionString(isUploadRead, readAll, isDelete, deleteAll);

      if (targetPermission !== newPermission) {
        setTargetPermission(newPermission);
      }
    }
  }, [isUploadRead, isDelete, readAll, deleteAll]);

  useEffect(() => {
    if (!isUploadRead) {
      setIsDelete(false);
    }
  }, [isUploadRead]);

  return (
    <UserPermissionSection>
      <Input
        type={'checkbox'}
        checked={isUploadRead}
        styles={css`
          grid-area: data;
          input,
          span,
          label[data-type='checkbox'] {
            display: none;
          }
          label {
            margin-left: 10px;
          }
        `}
        disabled
      >
        Data Upload & Read
      </Input>
      <Input
        type={'radio'}
        name={'data'}
        checked={readAll}
        onChange={() => setReadAll(true)}
        styles={css`
          grid-area: readAll;
        `}
      >
        Read All (include PACS)
      </Input>
      <Input
        type={'radio'}
        name={'data'}
        checked={!readAll}
        onChange={() => setReadAll(false)}
        styles={css`
          grid-area: readMy;
        `}
      >
        Read Only my data
      </Input>
      <Input
        type={'checkbox'}
        checked={isDelete}
        onChange={() => setIsDelete(!isDelete)}
        styles={css`
          grid-area: delete;
        `}
      >
        Delete & Edit
      </Input>
      <Input
        type={'radio'}
        name={'delete'}
        checked={deleteAll}
        onChange={() => setDeleteAll(true)}
        styles={css`
          grid-area: delAll;
          input:disabled {
            & ~ span:before {
              display: none !important;
            }
          }
        `}
        disabled={!isDelete || !readAll}
      >
        Delete & Edit All (include PACS)
      </Input>
      <Input
        type={'radio'}
        name={'delete'}
        checked={!deleteAll}
        onChange={() => setDeleteAll(false)}
        styles={css`
          grid-area: delMy;
          input:disabled {
            & ~ span:before {
              display: none !important;
            }
          }
        `}
        disabled={!isDelete}
      >
        Delete & Edit Only my data
      </Input>
    </UserPermissionSection>
  );
};

export const UserPermissionSection = styled.section`
  min-width: 410px;
  display: grid;
  grid-gap: 0;
  grid-template-rows: repeat(4, 40px);
  grid-template-areas:
    'data readAll'
    'data readMy'
    'delete delAll'
    'delete delMy';
  background: #ffffff;
  border: 1px solid #e0e0e0;
  box-sizing: border-box;
  border-radius: 5px;
  margin-bottom: 5px;

  & > [data-type='checkbox'] {
    border-right: 1px dashed #e0e0e0;
    &:nth-of-type(1) {
      border-bottom: 1px solid #e0e0e0;
    }
  }
  & > [data-type='radio'] {
    &:nth-of-type(3) {
      border-bottom: 1px solid #e0e0e0;
    }
    &:nth-of-type(3n - 1) {
      border-bottom: 1px dashed #e0e0e0;
    }
  }
  & > div {
    input:disabled ~ span {
      background-color: grey !important;
      border: 1px solid grey;
      cursor: not-allowed;
    }
  }
  @media (max-width: 412px) {
    grid-template-areas:
      'data'
      'readAll'
      'readMy'
      'delete'
      'delAll'
      'delMy';
    grid-template-rows: repeat(6, 40px);
    min-width: 200px;
    & > [data-type='checkbox'] {
      border-right: unset;
      border-bottom: 1px dashed #e0e0e0;
    }
  }
`;

export default UserPermission;
